/**
 * MonthView Component
 *
 * View component for month mode in the Sales Timeline.
 * Displays four stacked compact WeekCharts with a shared legend.
 *
 * US-PE-006: Sales Timeline - Phase 3 Page Components
 */

import { WeekChartCompact } from './WeekChart'
import ChartLegend from './ChartLegend'
import { formatWeekLabel } from '@/utils/timeline-utils'
import { createDate } from '@/utils/dateUtils'

/**
 * Get week label from week data
 *
 * @param {Object} weekData - Week data object with weekStart and items
 * @returns {string} Formatted week label
 */
function getWeekLabel(weekData) {
  if (!weekData?.weekStart) return ''

  const start = weekData.weekStart
  const end = createDate(start.getTime())
  end.setDate(end.getDate() + 6)

  return formatWeekLabel(start, end)
}

/**
 * MonthView - Four weeks stacked compact chart view
 *
 * @param {Object} props
 * @param {Array} props.weeklyData - Array of week data objects from useTimelineData
 *   Each: { weekStart: Date, items: Array of daily data }
 * @param {Array} props.strains - Array of strain objects { id, name, color }
 * @param {boolean} [props.showAllStrains=true] - Show all strains stacked or single strain
 * @param {string|null} [props.selectedStrainId] - Selected strain ID for single strain view
 * @param {Object} [props.dateRange] - Date range for the view { start, end }
 * @param {string} [props.className] - Additional CSS classes
 */
export default function MonthView({
  weeklyData = [],
  strains = [],
  showAllStrains = true,
  selectedStrainId = null,
  className = ''
}) {
  // Collect all strains that have data across all weeks
  const allWeekItems = weeklyData.flatMap(week => week?.items || [])
  const strainsWithData = strains.filter(strain => {
    return allWeekItems.some(item =>
      item.supplyByStrain && item.supplyByStrain[strain.name] > 0
    )
  })

  // Handle empty data
  if (weeklyData.length === 0) {
    return (
      <div className={`${className}`}>
        <div className="bg-slate-50 rounded-lg border border-dashed border-slate-200 p-8 text-center">
          <span className="text-sm text-slate-500">No data for this period</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Shared Legend */}
      {showAllStrains && strainsWithData.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 px-4 py-3">
          <ChartLegend
            strains={strainsWithData}
            showDemandLine={true}
            compact={true}
          />
        </div>
      )}

      {/* Stacked Week Charts (Compact) */}
      <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
        {weeklyData.map((week, index) => (
          <div key={index} className="p-3">
            <WeekChartCompact
              data={week.items}
              strains={strains}
              showAllStrains={showAllStrains}
              selectedStrainId={selectedStrainId}
              weekLabel={getWeekLabel(week)}
              height={130}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
